/**
 * Procedurally renders the orange concentric-arc dot texture used as the
 * `.hero-section::before` background in the original site, then injects a
 * runtime stylesheet that swaps the placeholder URL for the generated PNG.
 *
 * Mirrors the canvas math in the original `script.js` — same wave sources,
 * spacing, fall-off and colour so the hero looks identical.
 */
export function injectHeroDotWave() {
  if (typeof document === "undefined") return;
  // Idempotent: don't re-render if we've already injected the texture
  if (document.getElementById("maltora-hero-wave-style")) return;

  const W = 1440;
  const H = 740;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  if (!ctx) return;

  const sp = 6;
  const r0 = 0.72;
  const cols = Math.ceil(W / sp) + 6;
  const rows = Math.ceil(H / sp) + 6;
  const s1x = -80;
  const s1y = -110;
  const s2x = 1150;
  const s2y = 820;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const bx = col * sp - sp;
      const by = row * sp - sp;

      const d1 = Math.sqrt((bx - s1x) ** 2 + (by - s1y) ** 2);
      const u1x = (bx - s1x) / d1;
      const u1y = (by - s1y) / d1;
      const a1 = 38 * Math.sin(d1 * 0.021);

      const d2 = Math.sqrt((bx - s2x) ** 2 + (by - s2y) ** 2);
      const u2x = (bx - s2x) / d2;
      const u2y = (by - s2y) / d2;
      const a2 = 22 * Math.sin(d2 * 0.017 + 1.4);

      const x = bx + u1x * a1 * 0.7 + u2x * a2 * 0.3;
      const y = by + u1y * a1 * 0.7 + u2y * a2 * 0.3;

      if (x < 0 || x > W || y < 0 || y > H) continue;

      const fx = 1 - Math.max(0, (x / W - 0.12) / 0.88);
      const fy = 1 - Math.max(0, (y / H - 0.08) / 0.92);
      const alpha = Math.pow(Math.min(fx, fy * 0.55 + 0.45), 1.5) * 0.32;
      if (alpha < 0.01) continue;

      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#E86A0D";
      ctx.beginPath();
      ctx.arc(x, y, r0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const url = c.toDataURL("image/png");
  const s = document.createElement("style");
  s.id = "maltora-hero-wave-style";
  s.textContent = `.hero-section::before{background-image:url('${url}')!important;background-size:cover!important;opacity:1!important;}`;
  document.head.appendChild(s);
}
